import { newToast } from '../components';
import { ResultadoMicrobiologiaDTO, CreateUpdateResultadoMicrobiologiaDTO, CreateUpdateGramDTO, CreateUpdateCultivoDTO, CreateUpdateMicroorganismoDTO, CreateUpdateAntibiogramaDTO, BusquedaResultadosPendientesDTO, EstadisticasPorEstadoDTO, ResumenResultadoDTO, MicrobiologiaReporteDTO, InfoTiemposReporte } from '../types/types.microbiology';
import useApi from '../utils/axios-pettitions';

export const useApiResultsMicrobiology = () => {
  const { runApi } = useApi();

  // ====================================================================
  // CONSULTA DE RESULTADOS
  // ====================================================================

  /**
   * Obtiene un resultado de microbiología por ID
   */
  const getResultadoById = async (rmiId: number) => {
    return runApi(`ResultsMicrobiology/GetById?rmiId=${rmiId}`, 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return null;
      } else if (res.internalCode === 101) {
        return res.data as ResultadoMicrobiologiaDTO;
      } else if (res.internalCode === 203) {
        newToast('WARNING', 'Resultado no encontrado', 3);
        return null;
      }
      return null;
    });
  };

  /**
   * Obtiene el resultado de microbiología por ID de resultado general
   */
  const getResultadoByResId = async (resId: number) => {
    return runApi(`ResultsMicrobiology/GetByResId?resId=${resId}`, 'GET', null, null).then((res) => {
      if (res.internalCode === 101) {
        return res.data as ResultadoMicrobiologiaDTO;
      }
      return null;
    });
  };

  /**
   * Obtiene resultados pendientes con filtros
   */
  const getResultadosPendientes = async (filtros: BusquedaResultadosPendientesDTO) => {
    return runApi('ResultsMicrobiology/GetPendientes', 'POST', null, filtros).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return [];
      } else if (res.internalCode === 101) {
        return res.data as ResultadoMicrobiologiaDTO[];
      }
      return [];
    });
  };

  /**
   * Obtiene todos los resultados de microbiología de una orden
   */
  const getResultadosByOrden = async (ordenId: number) => {
    return runApi(`ResultsMicrobiology/GetByOrden?ordenId=${ordenId}`, 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return [];
      } else if (res.internalCode === 101) {
        return res.data as ResultadoMicrobiologiaDTO[];
      }
      return [];
    });
  };

  // ====================================================================
  // CREACIÓN INICIAL
  // ====================================================================

  /**
   * Crea un nuevo resultado de microbiología
   */
  const crearResultado = async (datos: CreateUpdateResultadoMicrobiologiaDTO) => {
    return runApi('ResultsMicrobiology/Create', 'POST', null, datos).then((res) => {
      if (res.internalCode === 101) {
        return res.data as ResultadoMicrobiologiaDTO;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al crear resultado');
      }
    });
  };

  // ====================================================================
  // PASO 1: EXAMEN DIRECTO (GRAM)
  // ====================================================================

  /**
   * Captura o actualiza el resultado del Gram
   */
  const capturarGram = async (rmiId: number, gram: CreateUpdateGramDTO) => {
    // Construir el request según el DTO del backend
    const request = {
      RmiId: rmiId,
      Gram: gram,
    };

    return runApi('ResultsMicrobiology/CapturarGram', 'POST', null, request).then((res) => {
      if (res.internalCode === 102) {
        newToast('SUCCESS', 'Gram capturado correctamente', 3);
        return res.data as ResultadoMicrobiologiaDTO;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al capturar Gram');
      }
    });
  };

  /**
   * Valida el Gram (permite reporte preliminar)
   */
  const validarGram = async (rmiId: number) => {
    return runApi('ResultsMicrobiology/ValidarGram', 'POST', null, { rmiId }).then((res) => {
      if (res.internalCode === 102) {
        newToast('SUCCESS', 'Gram validado correctamente', 3);
        return res.data as ResultadoMicrobiologiaDTO;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al validar Gram');
      }
    });
  };

  // ====================================================================
  // PASO 2: CULTIVO
  // ====================================================================

  /**
   * Captura o actualiza el resultado del cultivo
   */
  const capturarCultivo = async (rmiId: number, cultivo: CreateUpdateCultivoDTO) => {
    const request = {
      RmiId: rmiId,
      Cultivo: cultivo,
    };
    return runApi('ResultsMicrobiology/CapturarCultivo', 'POST', null, request).then((res) => {
      if (res.internalCode === 102) {
        newToast('SUCCESS', 'Cultivo capturado correctamente', 3);
        return res.data as ResultadoMicrobiologiaDTO;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al capturar cultivo');
      }
    });
  };

  /**
   * Valida el cultivo
   */
  const validarCultivo = async (rmiId: number) => {
    return runApi('ResultsMicrobiology/ValidarCultivo', 'POST', null, { rmiId }).then((res) => {
      if (res.internalCode === 102) {
        newToast('SUCCESS', 'Cultivo validado correctamente', 3);
        return res.data as ResultadoMicrobiologiaDTO;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al validar cultivo');
      }
    });
  };

  /**
   * Desvalida el Gram (permite edición nuevamente)
   */
  const desvalidarGram = async (rmiId: number) => {
    const request = {
      RmiId: rmiId,
    };

    return runApi('ResultsMicrobiology/DesvalidarGram', 'POST', null, request).then((res) => {
      if (res.internalCode === 102) {
        return res.data as ResultadoMicrobiologiaDTO;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al desvalidar Gram');
      }
    });
  };

  /**
   * Desvalida el cultivo (permite edición nuevamente)
   */
  const desvalidarCultivo = async (rmiId: number) => {
    const request = {
      RmiId: rmiId,
    };

    return runApi('ResultsMicrobiology/DesvalidarCultivo', 'POST', null, request).then((res) => {
      if (res.internalCode === 102) {
        return res.data as ResultadoMicrobiologiaDTO;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al desvalidar cultivo');
      }
    });
  };

  // ====================================================================
  // PASO 3: IDENTIFICACIÓN/AISLAMIENTO
  // ====================================================================

  /**
   * Agrega un microorganismo aislado
   */
  const agregarMicroorganismo = async (rmiId: number, microorganismo: CreateUpdateMicroorganismoDTO) => {
    const request = {
      RmiId: rmiId,
      Microorganismo: microorganismo,
    };
    return runApi('ResultsMicrobiology/AgregarMicroorganismo', 'POST', null, request).then((res) => {
      if (res.internalCode === 101) {
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al agregar microorganismo');
      }
    });
  };

  /**
   * Actualiza un microorganismo
   */
  const actualizarMicroorganismo = async (mmoId: number, microorganismo: CreateUpdateMicroorganismoDTO) => {
    return runApi('ResultsMicrobiology/ActualizarMicroorganismo', 'POST', null, { mmoId, ...microorganismo }).then((res) => {
      if (res.internalCode === 102) {
        newToast('SUCCESS', 'Microorganismo actualizado correctamente', 3);
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al actualizar microorganismo');
      }
    });
  };

  /**
   * Elimina un microorganismo
   */
  const eliminarMicroorganismo = async (mmoId: number) => {
    const request = { Id: mmoId };
    return runApi('ResultsMicrobiology/EliminarMicroorganismo', 'POST', null, request).then((res) => {
      if (res.internalCode === 103) {
        return true;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        return false;
      }
    });
  };

  /**
   * Valida la identificación de un microorganismo
   */
  const validarIdentificacion = async (mmoId: number) => {
    const request = { MmoId: mmoId };
    return runApi('ResultsMicrobiology/ValidarIdentificacion', 'POST', null, request).then((res) => {
      if (res.internalCode === 102) {
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al validar identificación');
      }
    });
  };

  // ====================================================================
  // PASO 4: ANTIBIOGRAMA
  // ====================================================================

  /**
   * Agrega un antibiótico individual al antibiograma
   */
  const agregarAntibiotico = async (mmoId: number, antibiograma: CreateUpdateAntibiogramaDTO) => {
    return runApi('ResultsMicrobiology/AgregarAntibiotico', 'POST', null, { mmoId, ...antibiograma }).then((res) => {
      if (res.internalCode === 100) {
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al agregar antibiótico');
      }
    });
  };

  /**
   * Agrega un panel completo de antibióticos
   */
  const agregarPanelAntibioticos = async (mmoId: number, gramTipo: string) => {
    const request = {
      MmoId: mmoId,
      GramTipo: gramTipo,
    };
    return runApi('ResultsMicrobiology/AgregarPanelAntibiograma', 'POST', null, request).then((res) => {
      if (res.internalCode === 100) {
        newToast('SUCCESS', 'Panel de antibióticos agregado correctamente', 3);
        return res.data as any[];
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al agregar panel de antibióticos');
      }
    });
  };

  /**
   * Actualiza un antibiótico del antibiograma
   */
  const actualizarAntibiotico = async (mabId: number, antibiograma: CreateUpdateAntibiogramaDTO) => {
    const request = {
      MabId: mabId,
      Antibiograma: antibiograma,
    };
    return runApi('ResultsMicrobiology/ActualizarAntibiotico', 'POST', null, request).then((res) => {
      if (res.internalCode === 102) {
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al actualizar antibiótico');
      }
    });
  };

  /**
   * Elimina un antibiótico del antibiograma
   */
  const eliminarAntibiotico = async (mabId: number) => {
    return runApi('ResultsMicrobiology/EliminarAntibiotico', 'POST', null, { mabId }).then((res) => {
      if (res.internalCode === 103) {
        newToast('SUCCESS', 'Antibiótico eliminado correctamente', 3);
        return true;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        return false;
      }
    });
  };

  /**
   * Valida el antibiograma completo de un microorganismo
   */
  const validarAntibiograma = async (mmoId: number) => {
    const request = { MmoId: mmoId };
    return runApi('ResultsMicrobiology/ValidarAntibiograma', 'POST', null, request).then((res) => {
      if (res.internalCode === 102) {
        return res.data;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al validar antibiograma');
      }
    });
  };

  // ====================================================================
  // PASO 5: VALIDACIÓN FINAL
  // ====================================================================

  /**
   * Valida el resultado completo de microbiología
   */
  const validarResultadoFinal = async (rmiId: number) => {
    return runApi('ResultsMicrobiology/ValidarFinal', 'POST', null, { rmiId }).then((res) => {
      if (res.internalCode === 102) {
        newToast('SUCCESS', 'Resultado validado correctamente', 4);
        return res.data as ResultadoMicrobiologiaDTO;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al validar resultado');
      }
    });
  };

  /**
   * Reporta el resultado (cambia estado a Reportado)
   */
  const reportarResultado = async (rmiId: number) => {
    return runApi('ResultsMicrobiology/Reportar', 'POST', null, { rmiId }).then((res) => {
      if (res.internalCode === 102) {
        newToast('SUCCESS', 'Resultado reportado correctamente', 4);
        return res.data as ResultadoMicrobiologiaDTO;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al reportar resultado');
      }
    });
  };

  // ====================================================================
  // ESTADÍSTICAS Y REPORTES
  // ====================================================================

  /**
   * Obtiene estadísticas de resultados por estado
   */
  const getEstadisticas = async (fechaInicio: string, fechaFin: string) => {
    return runApi(`ResultsMicrobiology/GetEstadisticas?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`, 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return null;
      } else if (res.internalCode === 101) {
        return res.data as EstadisticasPorEstadoDTO;
      }
      return null;
    });
  };

  /**
   * Obtiene un resumen de resultados para dashboard
   */
  const getResumen = async () => {
    return runApi('ResultsMicrobiology/GetResumen', 'GET', null, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return null;
      } else if (res.internalCode === 101) {
        return res.data as ResumenResultadoDTO;
      }
      return null;
    });
  };

  // ====================================================================
  // GESTIÓN DE REPORTES (SISTEMA HÍBRIDO)
  // ====================================================================

  /**
   * Obtiene todos los reportes generados de un resultado
   */
  const getReportesByRmiId = async (rmiId: number) => {
    return runApi(`ResultsMicrobiology/GetReportes?rmiId=${rmiId}`, 'GET', null, null).then((res) => {
      if (res.internalCode === 101) {
        return res.data as MicrobiologiaReporteDTO[];
      } else if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return [];
      }
      return [];
    });
  };

  /**
   * Obtiene un reporte específico por ID
   */
  const getReporteById = async (rmrId: number) => {
    return runApi(`ResultsMicrobiology/GetReporteById?rmrId=${rmrId}`, 'GET', null, null).then((res) => {
      if (res.internalCode === 101) {
        return res.data as MicrobiologiaReporteDTO;
      } else if (res.internalCode === 104) {
        return null;
      } else if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return null;
      }
      return null;
    });
  };

  /**
   * Obtiene información de tiempos y alertas para un resultado
   */
  const getInfoTiemposReporte = async (rmiId: number) => {
    return runApi(`ResultsMicrobiology/GetInfoTiempos?rmiId=${rmiId}`, 'GET', null, null).then((res) => {
      if (res.internalCode === 101) {
        return res.data as InfoTiemposReporte;
      } else if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return null;
      }
      return null;
    });
  };

  /**
   * Calcula las horas transcurridas desde la creación de un resultado
   */
  const calcularHorasTranscurridas = async (rmiId: number) => {
    return runApi(`ResultsMicrobiology/CalcularHorasTranscurridas?rmiId=${rmiId}`, 'GET', null, null).then((res) => {
      if (res.internalCode === 101) {
        return res.data.Horas as number;
      } else if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 3);
        return 0;
      }
      return 0;
    });
  };

  /**
   * Genera un reporte manualmente (botón del usuario)
   */
  const generarReporteManual = async (rmiId: number, tipoReporte: 'Preliminar1' | 'Preliminar2' | 'Final') => {
    const request = {
      RmiId: rmiId,
      TipoReporte: tipoReporte,
    };

    return runApi('ResultsMicrobiology/GenerarReporteManual', 'POST', null, request).then((res) => {
      console.log('Respuesta de generación de reporte:', res);
      if (res.internalCode === 101) {
        newToast('SUCCESS', `Reporte ${tipoReporte} generado correctamente`, 4);
        return res.data as MicrobiologiaReporteDTO;
      } else if (res.internalCode === 209) {
        // Error de validación (ej: ya existe el reporte)
        newToast('WARNING', res.message, 4);
        throw new Error(res.message);
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        throw new Error('Error al generar reporte');
      }
    });
  };

  /**
   * Marca un reporte como reportado (enviado al médico/HIS)
   */
  const marcarReporteComoReportado = async (rmrId: number) => {
    const request = { RmrId: rmrId };

    return runApi('ResultsMicrobiology/MarcarReporteReportado', 'POST', null, request).then((res) => {
      if (res.internalCode === 102) {
        newToast('SUCCESS', 'Reporte marcado como reportado', 3);
        return true;
      } else if (res.internalCode === 209) {
        newToast('ERROR', res.message, 4);
        return false;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        return false;
      }
    });
  };

  /**
   * Marca un reporte como impreso
   */
  const marcarReporteComoImpreso = async (rmrId: number) => {
    const request = { RmrId: rmrId };

    return runApi('ResultsMicrobiology/MarcarReporteImpreso', 'POST', null, request).then((res) => {
      if (res.internalCode === 102) {
        newToast('SUCCESS', 'Reporte marcado como impreso', 3);
        return true;
      } else if (res.internalCode === 209) {
        newToast('ERROR', res.message, 4);
        return false;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        return false;
      }
    });
  };

  /**
   * Elimina un reporte (solo para correcciones, requiere permiso)
   */
  const eliminarReporte = async (rmrId: number) => {
    const request = { RmrId: rmrId };

    return runApi('ResultsMicrobiology/EliminarReporte', 'POST', null, request).then((res) => {
      if (res.internalCode === 103) {
        newToast('SUCCESS', 'Reporte eliminado correctamente', 3);
        return true;
      } else if (res.internalCode === 209) {
        newToast('ERROR', res.message, 4);
        return false;
      } else {
        newToast('ERROR', res.messageDetails || res.message, 5);
        return false;
      }
    });
  };

  return {
    // Consultas
    getResultadoById,
    getResultadoByResId,
    getResultadosPendientes,
    getResultadosByOrden,

    // Creación
    crearResultado,

    // Gram
    capturarGram,
    validarGram,
    desvalidarGram,

    // Cultivo
    capturarCultivo,
    validarCultivo,
    desvalidarCultivo,

    // Identificación
    agregarMicroorganismo,
    actualizarMicroorganismo,
    eliminarMicroorganismo,
    validarIdentificacion,

    // Antibiograma
    agregarAntibiotico,
    agregarPanelAntibioticos,
    actualizarAntibiotico,
    eliminarAntibiotico,
    validarAntibiograma,

    // Validación Final
    validarResultadoFinal,
    reportarResultado,

    // Estadísticas
    getEstadisticas,
    getResumen,

    getReportesByRmiId,
    getReporteById,
    getInfoTiemposReporte,
    calcularHorasTranscurridas,
    generarReporteManual,
    marcarReporteComoReportado,
    marcarReporteComoImpreso,
    eliminarReporte,
  };
};
